// src/components/Sections/Projects/Projects.jsx
import React, { useState, useEffect, useMemo, useRef } from "react";
import "./projects.css";
import { projects as dataGalery } from "../../../json/sections/projects/proyects";
import Banner from "../../Layout/Banner/Banner";
import { Link } from "react-router-dom";
import Pagination from "../../Elements/Pagination/Pagination";
import { FaArrowRight } from "react-icons/fa6";
import CityWatter from "../../Layout/cityWatter/cityWatter";
import { dataCityWatter } from "../../../json/layout/cityWatter";
import Lightbox from "../../Layout/Lightbox/Lightbox";

const formatCategory = (category) =>
  category.charAt(0).toUpperCase() + category.slice(1).toLowerCase();

const Projects = () => {
  // ─────────── 1) Verificar datos
  const isValidData = dataGalery?.categories && dataGalery?.images;

  // ─────────── 2) Estados: categoría seleccionada y paginación
  const [selectedCategory, setSelectedCategory] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [lightboxIdx, setLightboxIdx] = useState(-1);

  // ─────────── 3) Responsive width
  const [width, setWidth] = useState(window.innerWidth);
  useEffect(() => {
    const onResize = () => setWidth(window.innerWidth);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  // ─────────── 4) Categories array memo (solo depende de isValidData)
  const categories = useMemo(
    () => (isValidData ? dataGalery.categories : []),
    [isValidData]
  );

  // ─────────── 5) Número de categorías por fila
  const itemsPerRow = useMemo(() => {
    if (width <= 768) return 1;
    if (width <= 1024) return 4;
    return Math.ceil(categories.length / 2);
  }, [width, categories.length]);

  // ─────────── 6) Chunk de categorías
  const rowsCategories = useMemo(() => {
    const rows = [];
    for (let i = 0; i < categories.length; i += itemsPerRow) {
      rows.push(categories.slice(i, i + itemsPerRow));
    }
    return rows;
  }, [categories, itemsPerRow]);

  // ─────────── 7) Scroll ref y control de página previa
  const galleryRef = useRef(null);
  const prevPageRef = useRef();

  // ─────────── 8) Inicializar selección al montar
  useEffect(() => {
    if (isValidData && categories.length > 0) {
      setSelectedCategory(formatCategory(categories[0]));
      setCurrentPage(1);
    }
  }, [isValidData, categories]);

  // ─────────── 9) Scroll suave entre páginas
  useEffect(() => {
    if (
      prevPageRef.current !== undefined &&
      currentPage !== prevPageRef.current
    ) {
      galleryRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    }
    prevPageRef.current = currentPage;
  }, [currentPage]);

  // ─────────── 10) Filtrado y paginación de imágenes
  const imagesByCategory = useMemo(
    () => (isValidData ? dataGalery.images : {}),
    [isValidData]
  );
  const allImages = useMemo(() => {
    if (!isValidData || !selectedCategory) return [];
    return imagesByCategory[selectedCategory] || [];
  }, [isValidData, imagesByCategory, selectedCategory]);

  const itemsPerPage = 10;
  const totalPages = useMemo(
    () => Math.ceil(allImages.length / itemsPerPage),
    [allImages.length]
  );
  const startIdx = (currentPage - 1) * itemsPerPage;
  const paginatedImages = useMemo(
    () => allImages.slice(startIdx, startIdx + itemsPerPage),
    [allImages, startIdx]
  );

  // ─────────── 11) Precarga de imágenes y cálculo de ratio
  const [loadedImages, setLoadedImages] = useState([]);
  useEffect(() => {
    if (!isValidData) {
      setLoadedImages([]);
      return;
    }
    let cancelled = false;
    Promise.all(
      paginatedImages.map(
        (img) =>
          new Promise((resolve) => {
            const imageObj = new Image();
            imageObj.src = img.src;
            imageObj.onload = () =>
              resolve({
                id: img.id,
                src: img.src,
                alt: img.alt || "",
                ratio: imageObj.naturalWidth / imageObj.naturalHeight,
              });
            imageObj.onerror = () =>
              resolve({
                id: img.id,
                src: img.src,
                alt: img.alt || "",
                ratio: 1,
              });
          })
      )
    ).then((results) => {
      if (!cancelled) setLoadedImages(results);
    });
    return () => {
      cancelled = true;
    };
  }, [isValidData, paginatedImages]);

  // ─────────── 12) División alterna en filas (3/2)
  const chunkedRows = useMemo(() => {
    if (!isValidData) return [];
    const rows = [];
    let i = 0;
    let useThree = true;
    while (i < loadedImages.length) {
      const size = useThree ? 3 : 2;
      rows.push(loadedImages.slice(i, i + size));
      i += size;
      useThree = !useThree;
    }
    return rows;
  }, [isValidData, loadedImages]);

  if (!isValidData) {
    return <div>Error: No se pudieron cargar los datos.</div>;
  }

  // ─────────── 13) Dinámica del título según selección
  const originalCategory =
    categories.find((c) => formatCategory(c) === selectedCategory) || "";
  const splitAt = originalCategory.lastIndexOf(" ");
  const h2 =
    splitAt > 0 ? originalCategory.slice(0, splitAt) : originalCategory;
  const h3 = splitAt > 0 ? originalCategory.slice(splitAt + 1) : "";

  // ─────────── 14) Header estático (texto y botón)
  const { text, textButton, urlButton } = dataGalery.header;

  // El arreglo plano de todas las imágenes actuales de la categoría seleccionada
  const flatImages = allImages;

  // Funciones de navegación
  const handlePrev = () => setLightboxIdx(idx => (idx > 0 ? idx - 1 : idx));
  const handleNext = () => setLightboxIdx(idx => (idx < flatImages.length - 1 ? idx + 1 : idx));
  const handleClose = () => setLightboxIdx(-1);

  return (
    <>
      <Banner />
      <div className="gallery-container container-fluid">
        {/* HEADER DINÁMICO */}
        <header className="gallery-header">
          <h2 className="line-height-clss">{h2}</h2>
          <h3 className="line-height-clss">{h3}</h3>
          <div className="container">
            <div className="header-row row align-items-center my-4">
              <div className="col-md-2" />
              <div className="col-md-4">
                <div
                  className="header-text line-height-clss"
                  dangerouslySetInnerHTML={{ __html: text }}
                />
              </div>
              <div className="col-md-4 text-md-center">
                <Link to={urlButton} className="btn-quote btn-general">
                  {textButton} <FaArrowRight className="card-arrow" />
                </Link>
              </div>
              <div className="col-md-2" />
            </div>
          </div>
          {/* CATEGORÍAS */}
          <nav className="categories">
            {rowsCategories.map((row, rowIdx) => (
              <div
                key={rowIdx}
                className={`category-row ${rowIdx < rowsCategories.length - 1 ? "with-separator" : ""
                  }`}
              >
                {row.map((category) => {
                  const formatted = formatCategory(category);
                  const words = category.split(" ");
                  const last = words.pop();
                  const first = words.join(" ");
                  return (
                    <button
                      key={category}
                      className={`category-button ${selectedCategory === formatted ? "active" : ""
                        }`}
                      onClick={() => {
                        setSelectedCategory(formatted);
                        setCurrentPage(1);
                      }}
                    >
                      {first}
                      <br />
                      {last}
                    </button>
                  );
                })}
              </div>
            ))}
          </nav>
        </header>
        {/* GALERÍA DE IMÁGENES */}
        <div className="image-gallery" ref={galleryRef}>
          {chunkedRows.length > 0 ? (
            chunkedRows.map((rowImages, rowIdx) => (
              <div key={rowIdx} className="image-row">
                {rowImages.map((img) => {
                  const realIdx = flatImages.findIndex(image => image.id === img.id); // Encuentra el índice global

                  return (
                    <div
                      key={img.id}
                      className="image-item"
                      style={{ flex: `${img.ratio} 1 0%` }}
                      onClick={() => setLightboxIdx(realIdx)}
                      tabIndex={0}
                    >
                      <img src={img.src} alt={img.alt} />
                    </div>
                  );
                })}

              </div>
            ))
          ) : (
            <p>No hay imágenes disponibles para esta categoría.</p>
          )}
        </div>
        {/* PAGINACIÓN */}
        {totalPages > 1 && (
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={setCurrentPage}
          />
        )}
        {lightboxIdx !== -1 && (
          <Lightbox
            images={flatImages}
            index={lightboxIdx}
            onClose={handleClose}
            onPrev={handlePrev}
            onNext={handleNext}
          />
        )}

        <CityWatter data={dataCityWatter} />
      </div>
    </>
  );
};

export default Projects;
